import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase/config';

const Navbar = () => {
  const { currentUser, userDetails } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login'); 
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const getInitials = () => {
    if (userDetails && userDetails.firstName && userDetails.lastName) {
      return `${userDetails.firstName.charAt(0)}${userDetails.lastName.charAt(0)}`.toUpperCase();
    } else if (currentUser && currentUser.email) {
      return currentUser.email.charAt(0).toUpperCase();
    } else {
      return 'U'; // Default initial
    }
  };

  const activeClassName = "bg-primary bg-opacity-10 text-primary dark:bg-indigo-700 dark:text-white px-3 py-2 rounded-md text-sm font-medium";
  const inactiveClassName = "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium";
  const mobileActiveClassName = "bg-primary bg-opacity-10 text-primary dark:bg-indigo-700 dark:text-white block px-3 py-2 rounded-md text-base font-medium";
  const mobileInactiveClassName = "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white block px-3 py-2 rounded-md text-base font-medium";

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const profileMenu = document.getElementById('user-menu-button');
      const profileDropdown = document.getElementById('user-profile-dropdown');
      if (profileMenu && !profileMenu.contains(event.target as Node) && 
          profileDropdown && !profileDropdown.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };

    if (isProfileOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    } else {
      document.removeEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isProfileOpen]);

  return (
    <nav className="bg-white dark:bg-gray-800 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and main nav */}
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Link to="/" className="text-xl font-bold text-primary dark:text-indigo-400">
                Million Miles
              </Link>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                {currentUser && (
                  <>
                    <NavLink
                      to="/dashboard"
                      className={({ isActive }) => isActive ? activeClassName : inactiveClassName}
                    >
                      Dashboard
                    </NavLink>
                    <NavLink
                      to="/miles"
                      className={({ isActive }) => isActive ? activeClassName : inactiveClassName}
                    >
                      Log Miles
                    </NavLink>
                     <NavLink
                      to="/donate"
                      className={({ isActive }) => isActive ? activeClassName : inactiveClassName}
                    >
                      Donate
                    </NavLink>
                    <NavLink
                      to="/leaderboards"
                      className={({ isActive }) => isActive ? activeClassName : inactiveClassName}
                    >
                      Leaderboards
                    </NavLink>
                    {userDetails?.teamId && (
                       <NavLink
                        to={`/teams/${userDetails.teamId}`}
                        className={({ isActive }) => isActive ? activeClassName : inactiveClassName}
                      >
                        My Team
                      </NavLink>
                    )}
                    {userDetails?.isAdmin && (
                      <NavLink
                        to="/admin"
                        className={({ isActive }) => isActive ? activeClassName : inactiveClassName}
                      >
                        Admin
                      </NavLink>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Right side: Auth buttons or Profile dropdown */}
          <div className="hidden md:block">
            <div className="ml-4 flex items-center md:ml-6">
              {!currentUser ? (
                <div className="space-x-2">
                  <Link to="/login" className="btn btn-outline btn-sm">
                    Login
                  </Link>
                  <Link to="/register" className="btn btn-primary btn-sm">
                    Register
                  </Link>
                </div>
              ) : (
                <div className="ml-3 relative">
                  <div>
                    <button
                      type="button"
                      className="max-w-xs bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-800 focus:ring-primary dark:focus:ring-indigo-500 h-8 w-8"
                      id="user-menu-button"
                      aria-expanded={isProfileOpen}
                      aria-haspopup="true"
                      onClick={() => setIsProfileOpen(!isProfileOpen)}
                    >
                      <span className="sr-only">Open user menu</span>
                      <span className="text-xs font-medium text-gray-700 dark:text-white">
                         {getInitials()}
                      </span>
                    </button>
                  </div>

                  {isProfileOpen && (
                    <div
                      id="user-profile-dropdown"
                      className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                      role="menu"
                      aria-orientation="vertical"
                      aria-labelledby="user-menu-button"
                      tabIndex={-1}
                    >
                      <span className="block px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
                        Signed in as
                        <strong className="block truncate">{currentUser.email}</strong>
                      </span>
                      <NavLink
                        to="/profile"
                        className={({ isActive }) => `${isActive ? 'bg-gray-100 dark:bg-gray-700' : ''} block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700`}
                        role="menuitem"
                        tabIndex={-1}
                        onClick={() => setIsProfileOpen(false)} 
                      >
                        Profile & Settings
                      </NavLink>
                       <NavLink
                        to="/invite"
                        className={({ isActive }) => `${isActive ? 'bg-gray-100 dark:bg-gray-700' : ''} block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700`}
                        role="menuitem"
                        tabIndex={-1}
                        onClick={() => setIsProfileOpen(false)} 
                      >
                        Invite Friends
                      </NavLink>
                      <button
                        onClick={() => { handleLogout(); setIsProfileOpen(false); }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        role="menuitem"
                        tabIndex={-1}
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="-mr-2 flex md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              type="button"
              className="bg-gray-100 dark:bg-gray-700 inline-flex items-center justify-center p-2 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 dark:focus:ring-offset-gray-800 focus:ring-primary dark:focus:ring-indigo-500"
              aria-controls="mobile-menu"
              aria-expanded={isOpen}
            >
              <span className="sr-only">Open main menu</span>
              {isOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu, show/hide based on menu state. */}
      {isOpen && (
        <div className="md:hidden" id="mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {currentUser ? (
              <>
                <NavLink
                  to="/dashboard"
                  className={({ isActive }) => isActive ? mobileActiveClassName : mobileInactiveClassName}
                   onClick={() => setIsOpen(false)}
                >
                  Dashboard
                </NavLink>
                 <NavLink
                  to="/miles"
                  className={({ isActive }) => isActive ? mobileActiveClassName : mobileInactiveClassName}
                   onClick={() => setIsOpen(false)}
                >
                  Log Miles
                </NavLink>
                <NavLink
                  to="/donate"
                  className={({ isActive }) => isActive ? mobileActiveClassName : mobileInactiveClassName}
                  onClick={() => setIsOpen(false)}
                >
                  Donate
                </NavLink>
                <NavLink
                  to="/leaderboards"
                  className={({ isActive }) => isActive ? mobileActiveClassName : mobileInactiveClassName}
                   onClick={() => setIsOpen(false)}
                >
                  Leaderboards
                </NavLink>
                {userDetails?.teamId && (
                   <NavLink
                    to={`/teams/${userDetails.teamId}`}
                    className={({ isActive }) => isActive ? mobileActiveClassName : mobileInactiveClassName}
                    onClick={() => setIsOpen(false)}
                  >
                    My Team
                  </NavLink>
                )}
                 {userDetails?.isAdmin && (
                   <NavLink
                    to="/admin"
                     className={({ isActive }) => isActive ? mobileActiveClassName : mobileInactiveClassName}
                     onClick={() => setIsOpen(false)}
                  >
                    Admin
                  </NavLink>
                )}
                 <NavLink
                  to="/profile"
                  className={({ isActive }) => isActive ? mobileActiveClassName : mobileInactiveClassName}
                  onClick={() => setIsOpen(false)}
                >
                  Profile & Settings
                </NavLink>
                 <NavLink
                  to="/invite"
                  className={({ isActive }) => isActive ? mobileActiveClassName : mobileInactiveClassName}
                  onClick={() => setIsOpen(false)}
                >
                  Invite Friends
                </NavLink>
                <button
                  onClick={() => { handleLogout(); setIsOpen(false); }}
                  className={`${mobileInactiveClassName} w-full text-left mt-1`}
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <NavLink
                  to="/login"
                  className={({ isActive }) => isActive ? mobileActiveClassName : mobileInactiveClassName}
                  onClick={() => setIsOpen(false)}
                >
                  Login
                </NavLink>
                <NavLink
                  to="/register"
                  className={({ isActive }) => isActive ? mobileActiveClassName : mobileInactiveClassName}
                   onClick={() => setIsOpen(false)}
                >
                  Register
                </NavLink>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar; 